// Story 6.1 (ADO-877) — auth bootstrap (AC2, AC4).
// Story 6.3 (ADO-879) — session lifecycle: silent-refresh wiring, logout,
// session restore, claim-derived session context, bootstrap timeout.
//
// This is a PLAIN component, NOT a React Context provider (project-context:
// "no Context API for global state"). It runs the one-time auth bootstrap as a
// side effect and writes the result into the Zustand stores; children read state
// from `useAuthStore` / `useSessionStore` directly. It renders its children
// unchanged.
//
// Bootstrap:
//   resolveAuthConfig()
//     ├─ not configured → store.setUnconfigured(missing); NO client, NO session (AC4)
//     └─ configured     → createClient
//                         → (handle redirect callback) OR (restore session, 6.3)
//                         → read session → register token provider + refresher
//                         → load session context (claims + TenantContext read)
//                         → store.setReady(... incl. logout)

import { type ReactNode, useEffect } from 'react';
import {
  registerAccessTokenProvider,
  registerAccessTokenRefresher,
  registerSessionTerminationHandler,
  resetAccessTokenProvider,
} from '../api/shoplink-api';
import { fetchTenantSession } from '../api/tenant-context.client';
import { fetchSessionDepotNames } from '../api/session-depots.client';
import { applyOrderingChoices, fetchOrderingChoicesOrNull } from '../ordering/orderingSession';
import { resolveAuthConfig } from './auth-config';
import {
  clearPersistedSession,
  completeRedirectCallback,
  createClient,
  forceRefreshAccessToken,
  getAccessToken,
  getSessionClaims,
  hasRedirectCallbackParams,
  isActivationLinkError,
  isFederatedSub,
  isTerminalAuthError,
  logout as clientLogout,
  requestPasswordChangeEmail,
  requestPasswordResetEmail,
  restoreSession,
  signIn as clientSignIn,
} from './auth0-client';
import type { Auth0RuntimeConfig } from './auth-config';
import { useAuthStore } from './useAuthStore';
import { useSessionStore } from './useSessionStore';
// Story 7-FU (ADO-1750), AC2 — the per-user feature stores cleared on logout so an
// in-SPA re-auth cannot show the prior user's hearts, suggestions, stock or basket.
import { useFavouritesStore } from '../favourites/useFavouritesStore';
import { useSubstitutesStore } from '../substitutes/useSubstitutesStore';
import { useRecommendationsStore } from '../recommendations/useRecommendationsStore';
import { useAvailabilityStore } from '../availability/useAvailabilityStore';
import { useBasketStore } from '../basket/useBasketStore';
import { usePriceStore } from '../pricing/usePriceStore';
import { useHhtOrdersStore } from '../hht/useHhtOrdersStore';
import { useHhtSendGuardStore } from '../hht/useHhtSendGuardStore';
import { resetAutoSendOutcomes } from '../basket/useAutoSendOutcomesStore';
import { useHhtWaitingCountStore } from '../hht/useHhtWaitingCountStore';

export interface AuthProviderProps {
  children: ReactNode;
}

/** Upper bound on the auth bootstrap so a stalled network never hangs the shell. */
const BOOTSTRAP_TIMEOUT_MS = 15_000;

// Bug 1192 review — the boot-time token-servability probe self-heals a TRANSIENT
// failure (a network blip / momentary token-endpoint error at load) with a small
// bounded retry before settling signed-out, rather than dropping the session for
// the whole page load. Terminal failures never retry. Kept well inside the 15s
// bootstrap timeout.
const BOOT_PROBE_ATTEMPTS = 2;
const BOOT_PROBE_RETRY_DELAY_MS = 500;
const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

type Store = ReturnType<typeof useAuthStore.getState>;
type Client = Awaited<ReturnType<typeof createClient>>;

// ── Bug 1180 (ADO-1180) — page-load-scoped single-flight bootstrap ───────────
//
// The bootstrap guard used to be a per-instance `started` useRef plus an
// in-closure `cancelled` flag. Under React <StrictMode> (dev only) the mount
// effect is double-invoked setup→cleanup→setup and the ref SURVIVES the
// remount: cleanup #1 permanently cancelled the first (only) bootstrap, setup
// #2 short-circuited on the ref, and the shell hung on `initializing` forever
// (the 15s timeout never fired — the wrapped promise resolved normally after
// the early isCancelled() return). Hoisting the single-flight out of the
// component lets a StrictMode remount RE-ATTACH to the in-flight bootstrap
// instead of being blocked by it: setReady lands in the GLOBAL Zustand store,
// so whichever mount survives observes it. This state is page-load-scoped by
// design (the SPA bootstraps once per document); tests reset it via
// `resetAuthBootstrapForTesting`.
//
// Bug 1192 (ADO-1192) — the singleton lives on `globalThis`, NOT at module
// scope: a Vite HMR edit of this file re-evaluates the module, and
// module-scoped state would fork into two copies — the OLD copy's effect
// cleanup saw ITS OWN mount counter hit 0 and tore down the SHARED token seams
// (`shoplink-api` is not re-evaluated) while the NEW copy had a live mount.
// `globalThis` has exactly the same per-document lifetime the module scope
// had, but survives module re-evaluation, so every copy shares one state.

interface BootstrapState {
  /** True once the bootstrap must no longer mutate global state (Story 6.3). */
  cancelled: boolean;
  /** True once the bootstrap promise has settled (success OR error path done). */
  settled: boolean;
  /**
   * True when the cancellation came from a genuine final unmount (teardown),
   * as opposed to a failure/timeout. Only a teardown-cancelled run may re-arm
   * — re-arming on a failure-cancelled run would retry a failing bootstrap in
   * a loop and wipe its error surface.
   */
  tornDown: boolean;
}

interface AuthBootstrapSingleton {
  /** The single-flight bootstrap run (null until the first mount starts one). */
  run: BootstrapState | null;
  /**
   * DA-2 (Bug 1180) — `completeRedirectCallback` consumes a single-use Auth0
   * authorization code. The single-flight already makes the bootstrap run at
   * most once per page load, but the code-consumption guard is a distinct
   * invariant (the original reason the `started` ref existed), so it gets its
   * own flag, decoupled from the effect lifecycle.
   */
  redirectCallbackConsumed: boolean;
  /**
   * Live AuthProvider mounts. StrictMode's synchronous cleanup→setup re-invoke
   * makes this dip to 0 and immediately return to 1 within the same effects
   * flush; only a GENUINE final unmount leaves it at 0 by the time a microtask
   * runs (see the effect cleanup below).
   */
  activeMounts: number;
}

const AUTH_BOOTSTRAP_KEY = '__shoplinkAuthBootstrap';

type BootstrapHost = typeof globalThis & {
  [AUTH_BOOTSTRAP_KEY]?: AuthBootstrapSingleton;
};

/** The shared singleton — one per document, shared by every module copy. */
function bootstrapSingleton(): AuthBootstrapSingleton {
  const host = globalThis as BootstrapHost;
  host[AUTH_BOOTSTRAP_KEY] ??= {
    run: null,
    redirectCallbackConsumed: false,
    activeMounts: 0,
  };
  return host[AUTH_BOOTSTRAP_KEY];
}

/**
 * Test-only seam — clears the shared single-flight so each test gets a fresh
 * bootstrap (mirrors how the Zustand stores are reset in `afterEach`). Never
 * called by production code.
 */
export function resetAuthBootstrapForTesting(): void {
  const shared = bootstrapSingleton();
  if (shared.run !== null) {
    // Cancel, don't merely orphan — a run left pending by one test must not
    // write into the NEXT test's freshly-reset global stores when it settles.
    shared.run.cancelled = true;
  }
  shared.run = null;
  shared.redirectCallbackConsumed = false;
  shared.activeMounts = 0;
  warnedTransientClasses.clear();
}

export function AuthProvider({ children }: AuthProviderProps) {
  useEffect(() => {
    const shared = bootstrapSingleton();
    shared.activeMounts += 1;

    // Start the bootstrap once per page load; a StrictMode remount (or any
    // additional mount — including a Vite-HMR module copy's) re-attaches to
    // the existing run. A bootstrap that was cancelled on a GENUINE unmount
    // and has fully settled is re-armed, so a fresh AuthProvider mount behaves
    // like a fresh page load.
    if (shared.run === null || (shared.run.cancelled && shared.run.settled)) {
      if (shared.run !== null) {
        // Fresh-page-load semantics on re-arm: the previous run's results
        // (ready/error surface, session context) are stale — its client is
        // abandoned and its token seams were torn down. Rendering them while
        // the new bootstrap runs would drive fetches through dead closures.
        useAuthStore.getState().reset();
        useSessionStore.getState().clear();
      }
      shared.run = startBootstrap();
    }

    return () => {
      shared.activeMounts -= 1;
      // DA-4 vs DA-1 (Bug 1180): a genuine final unmount must tear down the
      // token seams (an abandoned client must not resurrect a session), but
      // StrictMode's dev-only cleanup must NOT permanently cancel the shared
      // bootstrap. StrictMode re-runs setup SYNCHRONOUSLY after cleanup (same
      // effects flush), so deferring the teardown to a microtask lets the
      // remount re-register first; only a real unmount still sees zero live
      // mounts. Because `shared` lives on globalThis, an HMR-swapped copy's
      // mount counts too (Bug 1192) — the old copy's cleanup stands down.
      const state = shared.run;
      queueMicrotask(() => {
        if (shared.activeMounts === 0 && state !== null) {
          state.cancelled = true;
          state.tornDown = true;
          resetAccessTokenProvider();
        }
      });
    };
  }, []);

  return <>{children}</>;
}

/**
 * Kick off the page-load auth bootstrap. Returns its state handle immediately;
 * the async work runs detached and reports into the global auth store.
 */
function startBootstrap(): BootstrapState {
  const state: BootstrapState = { cancelled: false, settled: false, tornDown: false };
  const store = useAuthStore.getState();

  void (async () => {
    try {
      const result = resolveAuthConfig();
      if (!result.configured) {
        // AC4 — explicit not-configured state; never create a client or session.
        store.setUnconfigured(result.missing);
        return;
      }

      try {
        // Story 6.3 (Task 6) — bound the bootstrap so a stalled createClient /
        // checkStatus never leaves the shell stuck on "initializing".
        await withTimeout(
          bootstrapConfigured(store, result.config, () => state.cancelled),
          BOOTSTRAP_TIMEOUT_MS,
        );
      } catch (err) {
        if (state.cancelled) {
          // Already cancelled by a genuine teardown (or superseded by a
          // re-armed run) BEFORE this rejection landed — this run no longer
          // owns the global state. Writing an error surface (or resetting the
          // token seams a newer run may have re-registered) would stomp state
          // that isn't ours.
          return;
        }
        // The bootstrap failed or timed out — mark it cancelled so any late
        // completion of the still-running underlying work cannot flip the shell
        // back to `ready` (Story 6.3 invariant, preserved for DA-3: withTimeout
        // only rejects the WRAPPER promise; the underlying bootstrapConfigured
        // keeps running).
        state.cancelled = true;
        // The token seams are registered BEFORE loadSessionContext (which needs
        // the bearer for the tenant read), so a timeout/error firing after that
        // point would leave the bearer provider + refresher bound to this
        // abandoned client — a later apiFetch('/api/v1/*') would still attach a
        // token and could drive a loginWithRedirect. Tear the seams down so the
        // abandoned session cannot be resurrected (matches the bail-before-wiring
        // invariant below).
        resetAccessTokenProvider();
        // Story 6.4 (ADO-880, AC3) — an EXPIRED / already-used set-password link
        // surfaces here as a classified redirect-callback error. Route it to the
        // dedicated activation-error state (friendly recovery surface + re-request)
        // instead of the generic sign-in-failed screen.
        if (isActivationLinkError(err)) {
          store.setActivationError(makeRequestActivationLink(result.config));
          return;
        }
        // Never hang on "initializing": surface a bounded error reason so the
        // shell can render a recoverable state. Covers a failed client init, a
        // rejected/replayed redirect callback, Auth0 error redirects
        // (?error=access_denied&...), and a bootstrap timeout. No token/secret is
        // included in the reason.
        store.setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    } catch (err) {
      // Defensive backstop: a throw OUTSIDE the withTimeout wrapper (e.g. a
      // synchronous resolveAuthConfig failure) must not become an unhandled
      // rejection that strands the shell on `initializing` with no error
      // surface and no re-arm path.
      if (!state.cancelled) {
        state.cancelled = true;
        store.setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    } finally {
      state.settled = true;
      // Re-arm gap: a genuine teardown cancels via a MICROTASK, so a new mount
      // can re-attach BEFORE the cancellation lands (its cancelled-&&-settled
      // check saw neither flag) and would otherwise wait forever on a run that
      // bails before every store write. Detect it at settlement: torn down +
      // still the current run + a live mount → behave like a fresh page load.
      // Failure-cancelled runs (tornDown false) never re-arm — their error
      // surface must stand (DA-3).
      //
      // HMR note (Bug 1192 review): this closure — like the whole run — belongs
      // to the module copy that STARTED the bootstrap. After a Vite HMR swap,
      // re-attach/re-arm therefore executes the pre-edit copy's code; an edit
      // to the bootstrap logic itself needs a full page reload to take effect.
      // That is by design: DA-5 promises session/seam survival across HMR, not
      // hot-swapped bootstrap internals.
      const shared = bootstrapSingleton();
      if (state.tornDown && shared.run === state && shared.activeMounts > 0) {
        useAuthStore.getState().reset();
        useSessionStore.getState().clear();
        shared.run = startBootstrap();
      }
    }
  })();

  return state;
}

async function bootstrapConfigured(
  store: Store,
  config: Parameters<typeof createClient>[0],
  isCancelled: () => boolean,
): Promise<void> {
  const client = await createClient(config);

  // A run cancelled while createClient was in flight (timeout fired, or a
  // genuine teardown) must not proceed — most importantly it must not burn the
  // single-use authorization code below for a client nobody owns.
  if (isCancelled()) return;

  // Story 6.27 (ADO-1206) — the intended post-login route recovered from the
  // redirect callback's `appState.returnTo`; stays undefined on the restore /
  // no-callback path (→ origin landing, unchanged).
  let postLoginReturnTo: string | undefined;

  if (hasRedirectCallbackParams() && !bootstrapSingleton().redirectCallbackConsumed) {
    // DA-2 — the authorization code is single-use; consume it at most once per
    // page load no matter how many times the effect (or bootstrap) runs.
    bootstrapSingleton().redirectCallbackConsumed = true;
    postLoginReturnTo = await completeRedirectCallback(client);
  } else {
    // Story 6.3 (Task 5) — no callback in the URL → best-effort session warm-up.
    // The v2 SDK's checkSession NEVER rejects (cookie-gated, swallows inner
    // errors), so this cannot decide anything — the servability probe below is
    // authoritative. Kept as the 6.3 seam; the catch is a belt-and-braces guard
    // for fake clients / future SDK changes only.
    try {
      await restoreSession(client);
    } catch {
      /* best-effort only — the probe below decides */
    }
  }

  // Bug 1192 review — `authed` is derived from a SERVABLE session, not from the
  // bare presence of a cached profile. With the persisted cache, getUser() alone
  // would happily rehydrate a DEAD session (refresh token revoked server-side,
  // access token expired — the SDK evicts nothing on invalid_grant), rendering
  // an authenticated UI that collapses into a 401→redirect loop on first use.
  // Probe with getTokenSilently: cache-valid tokens resolve with no network; a
  // needed refresh runs the rotating-refresh grant; a TERMINAL rejection means
  // the session is genuinely gone → purge the persisted remains so they cannot
  // rehydrate again, and boot cleanly signed-out. A TRANSIENT rejection (network
  // blip at boot) also boots signed-out but keeps the cache — the next load with
  // connectivity restores the session.
  // Bail before the probe if this run was cancelled (timeout / teardown) while
  // the callback/restore above was in flight — a run that no longer owns the
  // global state must not probe, and above all must not PURGE the shared
  // persisted cache (a stale run's late purge could race a fresh sign-in
  // written by another tab).
  if (isCancelled()) return;

  let tokenServable = false;
  for (let attempt = 1; attempt <= BOOT_PROBE_ATTEMPTS; attempt++) {
    try {
      await getAccessToken(client);
      tokenServable = true;
      break;
    } catch (err) {
      if (isTerminalAuthError(err)) {
        // Re-check: the probe itself may have outlived a timeout; only the run
        // that still owns the bootstrap may destroy shared persisted state.
        if (isCancelled()) return;
        await clearPersistedSession(client);
        break; // terminal — the session is genuinely gone; never retry.
      }
      // TRANSIENT (network blip / momentary token error): a bounded retry gives
      // a brief self-heal before settling signed-out. The cache is kept either
      // way, so a later load with connectivity still restores the session.
      warnTransientTokenFailure(err);
      if (attempt < BOOT_PROBE_ATTEMPTS) {
        await delay(BOOT_PROBE_RETRY_DELAY_MS);
        if (isCancelled()) return;
      }
    }
  }
  // Both halves required: a servable token AND a profile. (A cached profile
  // without a servable token is the dead-session case above; a token without a
  // profile never occurs with the real SDK but must not read as signed-in.)
  const user = tokenServable ? ((await client.getUser()) ?? null) : null;
  const authed = user !== null;

  // Story 6.3 — if the bootstrap already timed out / was torn down, bail BEFORE
  // touching any global state (token seams, session store, auth store). Wiring
  // them now would resurrect a session the shell has already abandoned.
  if (isCancelled()) return;

  if (authed) {
    // Wire the request layer's bearer token to this client (AC3 seam, Story 6.1).
    // Bug 1192 review — registered ONLY for an authenticated bootstrap: the
    // persisted cache is shared across tabs, so an unconditional provider would
    // let a signed-OUT tab silently attach a valid Bearer written later by a
    // sign-in in ANOTHER tab, feeding authenticated responses into a surface
    // presenting as anonymous. A signed-out tab keeps the default null provider
    // until its own (redirect-driven) bootstrap lands authenticated.
    //
    // Provider contract (closes 6.1 review D-3): a TERMINAL rejection
    // (login_required family — the session is genuinely gone) resolves null
    // silently; a TRANSIENT failure (network blip, Auth0 outage) emits a
    // throttled diagnostic first. Promise<string|null> stays intact — apiFetch
    // awaits this provider un-caught, so it must not throw.
    registerAccessTokenProvider(async () => {
      try {
        return await getAccessToken(client);
      } catch (err) {
        if (!isTerminalAuthError(err)) {
          warnTransientTokenFailure(err);
        }
        return null;
      }
    });

    // Story 6.3 (AC1/AC2) — wire the post-401 forced-refresh hook.
    registerAccessTokenRefresher(makeSessionRefresher(client, store));

    // A deactivation 403 from the API is TERMINAL (registry `inactive`, decision
    // D4) — unlike an expiry 401 it must NOT be refreshed/retried (the Auth0
    // session is still valid, so a refresh would succeed and the request would
    // loop). End the session with a full sign-out → branded login. Fired at most
    // once by the transport.
    registerSessionTerminationHandler(() => {
      void performFullLogout(client, store);
    });

    // Story 6.3 (AC4) — expose claim-derived session context once authenticated.
    await loadSessionContext(client);
  } else {
    useSessionStore.getState().clear();
  }

  // Re-check after the (awaited) session-context load: a timeout that fired while
  // loadSessionContext was in flight must still win over this ready state.
  if (isCancelled()) return;

  // Story 6.6 (ADO-882, AC1/Task 3.2) — bind the authenticated "Change password"
  // trigger ONLY for a signed-in Auth0 database-connection identity. A user with
  // a federated `sub` (samlp|/oidc|/waad|/adfs|/pingfederate|) has no Auth0
  // password to change, so the affordance is hidden (null). Story 6.14 (ADO-1186,
  // D9): this gate is CORRECT-BUT-INERT in Phase 1 — staff are custom-DB users
  // (`sub = auth0|<uuid>`) who DO hold an Auth0-surfaced password, so they pass
  // the gate and see the affordance, which is the desired behaviour; the guard
  // stays for the post-Phase-1 federation return. The email is sourced from the
  // session here (no input) and trimmed so a whitespace-only/padded claim can't
  // bind a false affordance (mirrors ForgotPassword's `email.trim()` guard on
  // the logged-out path).
  const changeEmail = typeof user?.email === 'string' ? user.email.trim() : '';
  const requestPasswordChange =
    authed && changeEmail !== '' && !isFederatedSub(user?.sub ?? '')
      ? makeRequestPasswordChange(config, changeEmail)
      : null;

  store.setReady({
    isAuthenticated: authed,
    user,
    // Story 6.14 (ADO-1186, D9) — the single sign-in entry; staff are custom-DB
    // users on the same shared connection, no enterprise initiator.
    // Story 6.27 (ADO-1206) — forwards the optional deep-link `returnTo` as
    // `appState.returnTo` (see auth0-client.signIn).
    signIn: (returnTo?: string) => clientSignIn(client, returnTo),
    // Story 6.5 (ADO-881, AC1) — the logged-out "Forgot password?" trigger. Bound
    // to the resolved config only (no authenticated client needed); enumeration-safe
    // and a no-op send when the DB connection is unconfigured.
    requestPasswordReset: makeRequestPasswordReset(config),
    // Story 6.6 (ADO-882, AC1) — the authenticated, outcome-aware change-password
    // trigger (null unless a DB-connection identity is signed in; see above).
    requestPasswordChange,
    // Story 6.3 (AC3) — clear local session state FIRST (so no stale token is
    // attachable), then end the Auth0 (IdP) session and return to the branded
    // login on reload.
    logout: () => performFullLogout(client, store),
    // Story 6.27 (ADO-1206) — hand the recovered deep-link route to the router
    // (via the `/`-route App effect), but ONLY for an authenticated bootstrap:
    // a returnTo without a live session has nowhere safe to land, so a
    // non-authenticated ready state keeps the origin.
    postLoginReturnTo: authed ? (postLoginReturnTo ?? null) : null,
  });
}

// Bug 1192 review — transient token-failure diagnostics are throttled to once
// per error class per page load: the provider runs on EVERY api fetch, and an
// Auth0 outage would otherwise emit one identical warn per request exactly when
// the app is already degraded. Error class/code only — never token material
// (NFR15). Reset alongside the bootstrap seam for tests.
const warnedTransientClasses = new Set<string>();

function warnTransientTokenFailure(err: unknown): void {
  // Throttle key = the error CLASS (Auth0 `error` code, else the Error name) —
  // never the message, whose variable content would defeat the throttle.
  const code = (err as { error?: unknown } | null)?.error;
  const classKey = typeof code === 'string' ? code : err instanceof Error ? err.name : 'unknown';
  if (warnedTransientClasses.has(classKey)) return;
  warnedTransientClasses.add(classKey);
  console.warn(
    '[auth] access token unavailable (transient — request proceeds unauthenticated):',
    err instanceof Error ? `${err.name}: ${err.message}` : String(err),
  );
}

/**
 * Story 6.3 (AC1/AC2) — a single-flight forced-refresh hook for the transport.
 * Concurrent 401s share ONE in-flight refresh (refresh-token rotation race
 * guard). On a TERMINAL failure (session genuinely gone) it clears local session
 * state — INCLUDING the persisted cache (Bug 1192 review: without the purge the
 * revoked token material would linger in localStorage and rehydrate a
 * dead-session UI on the next load) — and redirects to Universal Login; on a
 * TRANSIENT failure it returns null (the 401 is surfaced) WITHOUT signing the
 * user out.
 */
function makeSessionRefresher(client: Client, store: Store): () => Promise<string | null> {
  let inFlight: Promise<string | null> | null = null;
  return () => {
    if (inFlight !== null) return inFlight;
    inFlight = (async () => {
      try {
        return await forceRefreshAccessToken(client);
      } catch (err) {
        if (isTerminalAuthError(err)) {
          // AC2 — clear local session, then redirect to branded Universal Login.
          endLocalSession(store);
          // Bug 1192 review — the session is terminally dead: purge its
          // persisted remains (local-only, no navigation) so an abandoned
          // Universal Login cannot leave live token material in localStorage.
          await clearPersistedSession(client);
          // The redirect navigates away on success; if it fails to start (popup/
          // redirect interference, URL build error) handle the rejection so it is
          // not an unhandled promise rejection AND the shell is not left stuck on
          // "initializing" (endLocalSession just reset the store). Surface a
          // recoverable error instead.
          clientSignIn(client).catch(() => {
            store.setError(
              'Your session ended and re-authentication could not be started. Please reload.',
            );
          });
        }
        return null;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };
}

/**
 * Story 6.5 (ADO-881, AC1/AC4) — build the enumeration-safe email-reset action
 * bound to the resolved config, over the shared `/dbconnections/change_password`
 * seam. When the Deloitte-owned DB `connection` is not configured the send
 * degrades to a no-op (the surface still shows the same generic confirmation —
 * never disclosing config state or account existence). Used for BOTH the 6.5
 * "Forgot password?" trigger and the 6.4 onboarding re-request (same call).
 */
function makeRequestPasswordReset(config: Auth0RuntimeConfig): (email: string) => Promise<void> {
  return async (email: string) => {
    if (config.dbConnection === undefined || config.dbConnection === '') return;
    await requestPasswordResetEmail({
      domain: config.domain,
      clientId: config.clientId,
      connection: config.dbConnection,
      email,
    });
  };
}

/**
 * Story 6.4 (ADO-880, AC3) onboarding re-request binder — the expired/used
 * set-password link re-send is the SAME shared seam as the 6.5 reset, so this is
 * an alias of {@link makeRequestPasswordReset}, not a second implementation.
 */
const makeRequestActivationLink = makeRequestPasswordReset;

/**
 * Story 6.6 (ADO-882, AC1/AC3) — build the OUTCOME-AWARE change-password action for
 * the authenticated user, bound to the resolved config + the session email (no
 * email input). Over the same `/dbconnections/change_password` seam as the reset,
 * but via {@link requestPasswordChangeEmail}, which REJECTS on failure so the
 * surface can show the AC3 initiation-failure message. When the Deloitte-owned DB
 * `connection` is not configured the change cannot be sent, so — for this
 * authenticated flow — surface the same failure rather than a false success (Dev
 * Notes scope boundary; contrast {@link makeRequestPasswordReset}'s silent no-op).
 */
function makeRequestPasswordChange(config: Auth0RuntimeConfig, email: string): () => Promise<void> {
  return async () => {
    if (config.dbConnection === undefined || config.dbConnection === '') {
      throw new Error('Password change is unavailable.');
    }
    await requestPasswordChangeEmail({
      domain: config.domain,
      clientId: config.clientId,
      connection: config.dbConnection,
      email,
    });
  };
}

/** Clear ALL local session state — auth store, token seams, session context.
 *
 *  Story 7-FU (ADO-1750), AC2 — ALSO reset every per-user feature store. This is
 *  the single choke point for both the store `logout` action and the deactivation-
 *  termination path (see `performFullLogout`), so resetting here (rather than at a
 *  call site) covers both. Auth0's full-redirect logout wipes module state on the
 *  document reload, but an SPA-local sign-out / in-SPA account switch does not, so
 *  without this a re-auth could show the PRIOR user's favourites, substitutes,
 *  recommendations, availability or basket. The substitutes/recommendations
 *  `reset()` bump a monotonic generation so an in-flight load resolving after the
 *  reset cannot resurrect a stale entry. (Basket included — D-4: it holds the most
 *  sensitive prior-session state, so it must not survive an account switch either.) */
function endLocalSession(store: Store): void {
  store.reset();
  resetAccessTokenProvider();
  useSessionStore.getState().clear();
  useFavouritesStore.getState().reset();
  useSubstitutesStore.getState().reset();
  useRecommendationsStore.getState().reset();
  useAvailabilityStore.getState().reset();
  useBasketStore.getState().reset();
  // Pricing migration 17 (ADO-2248): the prices are the session's too. The store key is the
  // entry and account with no tenant, and reset() also drops any answer still on the wire.
  usePriceStore.getState().reset();
  // Story 8.10 (ADO-2019): the HHT waiting count and details are per Customer, so they go
  // with the session (DV-39), and so does a send guard question still open (Q2).
  useHhtWaitingCountStore.getState().reset();
  useHhtOrdersStore.getState().reset();
  useHhtSendGuardStore.getState().reset();
  // Story 8.15 (ADO-2024): the Auto-send outcomes are the caller's carts', so they go too.
  resetAutoSendOutcomes();
}

/**
 * Full sign-out (Story 6.3 AC3): clear local session state FIRST (so no stale
 * token is attachable), then end the Auth0 (IdP) session and return to the
 * branded login on reload. Shared by the store's `logout` action and the
 * deactivation-termination handler. Never rejects — an IdP-logout failure
 * (returnTo not allow-listed, network error before navigation) surfaces a
 * recoverable error rather than hanging the shell on "Loading…". No token/secret
 * in the reason (NFR15).
 */
async function performFullLogout(client: Client, store: Store): Promise<void> {
  endLocalSession(store);
  try {
    await clientLogout(client);
  } catch (err) {
    store.setError(err instanceof Error ? err.message : 'Sign-out could not be completed');
  }
}

/**
 * Story 6.3 (AC4) — populate the non-authoritative session context; realigned
 * by Story 6.32 (ADO-1315) to the BWG claim-less model: identity (sub/name/
 * email) still comes from the ID token (standard OIDC), but tenant/role/
 * depots/accountType come from the SERVER session read
 * (`GET /membership/tenant-context` — the membership registry the API
 * resolves every request from). Fully defensive: a claims read failure leaves
 * the context cleared; the server read degrades to a cleared context slice
 * (null role/tenant, empty depots). Never throws.
 */
async function loadSessionContext(client: Client): Promise<void> {
  const session = useSessionStore.getState();
  let claims: Awaited<ReturnType<typeof getSessionClaims>>;
  try {
    claims = await getSessionClaims(client);
  } catch {
    // Claims unreadable → leave the context fully cleared. Do NOT proceed to the
    // server read: setting role/tenant on top of a cleared store would produce an
    // incoherent context (a role present with a null identity).
    session.clear();
    return;
  }
  session.setFromClaims(claims ?? null);
  // The server session read, the Story 7.22 (ADO-1703) depot-name read and the
  // Story 8.1 (ADO-2010) ordering-choices read are independent (each resolves the
  // caller server-side from the JWT, not from the store), so fetch them in parallel.
  // None of them throws: the first two degrade to a cleared/empty result, and a
  // failed choices read becomes the store's `error` state (the choice screen offers a
  // retry), distinct from an unlinked Shopper's empty 200 (A2.2).
  session.setOrderingLoading();
  const [serverSession, depotNames, orderingChoices] = await Promise.all([
    fetchTenantSession(),
    fetchSessionDepotNames(),
    fetchOrderingChoicesOrNull(),
  ]);
  session.setFromServerSession(serverSession);
  session.setDepotNames(depotNames);
  // Applied last: the remembered hint is keyed by the tenant the session read resolves,
  // and the sign-in choice (A1.1) needs it.
  applyOrderingChoices(orderingChoices, Date.now());
}

/** Reject `p` if it does not settle within `ms` (bootstrap hang guard). Exported for unit tests. */
export function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Authentication timed out')), ms);
    p.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        // Pass the ORIGINAL rejection reason through unchanged — Story 6.4 needs the
        // Auth0 `{ error, error_description }` shape intact to classify an expired
        // activation link (wrapping it in `new Error(String(err))` would erase it).
        reject(err);
      },
    );
  });
}

export default AuthProvider;
